/* eslint-disable no-case-declarations */
import { Component, Input, OnInit } from '@angular/core';
import { Observable, Subject, takeUntil } from 'rxjs';
import { Store } from '@ngxs/store';
// Modules
import {
	BroadcastService,
	ChangeEventStatusChannelAction,
	EVENT_STATUS,
	GlobalUtils,
	ORDER_EVENT_TYPE,
	PermissionService,
	SignalRState,
	ToasterType,
	UserContextModel
} from '@cv-invision/core';
import { PERMISSION } from '@cv-invision/cv.invision-views.ux';
// Models
import { OrderDetailModel } from '../../../state/model/order-detail.model';
import { ORDER_STATE } from '../../../model/quality.constant';
// Actions
import {
	SetOrderLocked,
	SetOrderStateEnum,
	SetOrderUnlocked
} from '../../../state/actions/quality-order-detail.actions';
// Services
import { QualityDocumentService } from '../../../service/document-quality.service';
import { OverviewSubHeaderService } from '../service/overview-sub-header.service';

@Component({
	selector: 'cv-ci-sub-header-lock-button',
	templateUrl: './lock-button.component.html',
	styleUrls: ['./lock-button.component.scss'],
	host: {
		class: 'd-inline-flex align-items-center gap-3',
		'[class.d-none]': '!isVisible'
	}
})
export class LockButtonComponent implements OnInit {
	@Input() orderInboxModel: OrderDetailModel;
	channels$: Observable<any> = this.store.select(SignalRState.channelEvents);

	unlockingOrder = false;
	userContext: UserContextModel;

	public tooltipPlacement = 'top';

	private atComponentDestroy = new Subject();

	get canShowEditUnlock(): boolean {
		return this.userPermision.hasPermission(PERMISSION.INTELLIGENCE_EDIT_ORDER);
	}

	get canForceUnlock(): boolean {
		return this.userPermision.hasPermission(PERMISSION.INTELLIGENCE_FORCEUNLOCK);
	}

	get isVisible(): boolean {
		return !!this.orderInboxModel?.locked || this.canShowEditUnlock;
	}

	get canToggleLock(): boolean {
		if (!this.canShowEditUnlock || !this.orderInboxModel) {
			return false;
		}
		if (!this.orderInboxModel.locked) {
			return true;
		}
		if (this.orderInboxModel.lockedBy?.id === this.userContext?.id) {
			return true;
		}
		return this.canForceUnlock;
	}

	get currentIconClass(): string {
		return this.orderInboxModel?.locked ? 'fa-lock' : 'fa-lock-open';
	}

	get nextIconClass(): string {
		return this.orderInboxModel?.locked ? 'fa-lock-open' : 'fa-lock';
	}

	get lockTooltip(): string {
		if (!this.orderInboxModel?.locked) {
			return 'Lock Order';
		}
		if (this.canToggleLock) {
			return 'Unlock Order';
		}
		return 'Read Information Only';
	}

	get lockedByDisplayName(): string {
		const lockedBy = this.orderInboxModel?.lockedBy;
		if (!lockedBy) {
			return '';
		}
		if (lockedBy.firstName === null && lockedBy.lastName === null) {
			return 'System';
		}
		return `${lockedBy.firstName || ''} ${lockedBy.lastName || ''}`.trim();
	}

	constructor(
		private store: Store,
		private broadcastService: BroadcastService,
		private qualityService: QualityDocumentService,
		private headerService: OverviewSubHeaderService,
		private userPermision: PermissionService
	) {
		this.tooltipPlacement = GlobalUtils.getTooltipPlacement();
	}

	ngOnInit(): void {
		this.userContext = this.store.selectSnapshot((state) => state.user);

		this.channels$.pipe(takeUntil(this.atComponentDestroy)).subscribe((channels: any) => {
			if (channels && channels[`orders:${this.orderInboxModel.id}`]) {
				channels[`orders:${this.orderInboxModel.id}`].events.forEach((channelEvent: any) => {
					let clearEvent = false;
					if (channelEvent.status === 'new') {
						switch (channelEvent.event) {
							// On order locked.
							case ORDER_EVENT_TYPE.ORDER_LOCKED:
								this.broadcastService.broadcast({
									name: ToasterType.SUCCESS,
									event: {
										message: this.headerService.getOrderMsg(channelEvent.data.user)
									}
								});
								const userData = {
									id: channelEvent.data.user.id,
									firstName: channelEvent.data.user.firstName,
									lastName: channelEvent.data.user.lastName
								};

								this.store.dispatch(new SetOrderLocked(new Date(), userData));
								this.store.dispatch(new SetOrderStateEnum(ORDER_STATE.INQUALITY));
								clearEvent = true;
								break;
							// On order unlocked.
							case ORDER_EVENT_TYPE.ORDER_UNLOCKED:
								this.broadcastService.broadcast({
									name: ToasterType.SUCCESS,
									event: {
										message: this.headerService.getOrderMsg(channelEvent.data.user, false)
									}
								});
								this.store.dispatch(new SetOrderUnlocked());
								clearEvent = true;
								break;
						}
						// Change event to old
						if (clearEvent) {
							const orderChannel = `orders:${this.orderInboxModel.id}`;
							this.store.dispatch(new ChangeEventStatusChannelAction(orderChannel, EVENT_STATUS.OLD));
						}
					}
				});
			}
		});
	}

	/**
	 * Toggle lock or unlock from the status icon.
	 */
	onToggleLock(): void {
		if (!this.canToggleLock || this.unlockingOrder) {
			return;
		}
		if (this.orderInboxModel.locked) {
			const isForceUnlock = this.orderInboxModel.lockedBy?.id !== this.userContext.id;
			this.onUnLockOrder(isForceUnlock);
			return;
		}
		this.lockOrder();
	}

	/**
	 * On button edit order click, lock the order.
	 */
	lockOrder(): void {
		this.qualityService.lockOrder(this.orderInboxModel.id).subscribe(
			(result) => {
				if (result.succeed) {
					this.broadcastService.broadcast({
						name: ToasterType.SUCCESS,
						event: {
							message: 'Order locked: Now only you can edit this order'
						}
					});
					this.store.dispatch(
						new SetOrderLocked(new Date(), {
							id: this.userContext.id,
							firstName: this.userContext.firstName,
							lastName: this.userContext.lastName
						})
					);
					if (this.orderInboxModel.stateEnum === ORDER_STATE.NOTREVIEWED) {
						this.store.dispatch(new SetOrderStateEnum(ORDER_STATE.INQUALITY));
					}
				} else if (result.message && result.message.length) {
					this.broadcastService.broadcast({
						name: ToasterType.ERROR,
						event: {
							error: result.message
						}
					});
				}
			},
			(error) => console.error(error)
		);
	}

	/**
	 * On button unlock order click.
	 * 1 - Save Order Document data
	 * 2 - Save Photos as primary.
	 * 3 - Unlock Order.
	 */
	onUnLockOrder(isForceUnlock: boolean = false): void {
		this.unlockingOrder = true;
		this.unlockOrder(isForceUnlock);
	}

	/**
	 * Call api to unlock order
	 */
	private unlockOrder(isForceUnlock: boolean = false): void {
		this.qualityService.unlockOrder(this.orderInboxModel.id, isForceUnlock).subscribe(
			(result: { succeed: any }) => {
				if (result.succeed) {
					this.broadcastService.broadcast({
						name: ToasterType.SUCCESS,
						event: {
							message: 'Order unlocked: Now anyone can edit this order'
						}
					});
					this.store.dispatch(new SetOrderUnlocked());
					this.unlockingOrder = false;
				} else {
					this.unlockingOrder = false;
				}
			},
			(error: any) => {
				console.error(error);
				this.unlockingOrder = false;
			}
		);
	}
}
